import React from 'react';
import { List } from 'immutable';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Linkify from 'react-linkify';
import { useSelector } from 'react-redux';

import { black } from 'utils/color-styles';
import Deed from 'entities/deed/model';
import Organization from 'entities/organization/model';
import VolunteerTime from 'entities/volunteerTime/model';
import { DateRangeWithTimeZone } from 'components/DeedListDateInfoWithStatus/DateRangeWithTimeZone';
import Box from 'retired/Box';
import { currencyFormat } from 'utils/currencyFormat';
import { selectUser } from 'entities/user/selectors';

import { AttendeesTable } from './AttendeesTable';

interface Props {
  deed: Deed;
  organization: Organization;
  volunteerTimes: List<VolunteerTime>;
  skills: any[];
}

const renderNeededSkills = (requiredSkills, skills) =>
  requiredSkills
    .map((skillId) => {
      const skill = skills.get(skillId);
      return skill ? skill.name : skillId;
    })
    .join(', ');

export const RolesAttendeesTable = ({ deed, organization, volunteerTimes, skills }: Props) => {
  const user = useSelector(selectUser);

  if (!deed.roles?.size) {
    return null;
  }

  return (
    <Box style={{ marginTop: 20, align: 'center' }}>
      <Typography
        type="headline"
        component="h3"
        style={{
          fontSize: 17,
          fontWeight: 500,
        }}
      >
        {deed.type === 'Project' ? 'Needed Roles' : 'Needed Shifts'}
      </Typography>
      {deed.roles.map((role) => {
        const applicants = role.get('applicants');
        const approved = role.get('approved');
        const rejected = role.get('rejected');
        const estimatedValue = role.getIn(['estimatedValueCurrencies', user.displayCurrency]);

        return (
          <div style={{ marginTop: 30 }} key={role.get('id')}>
            <Divider />
            <Typography
              component="p"
              style={{
                fontSize: 15,
                fontWeight: 500,
                marginBottom: 15,
                marginTop: 30,
              }}
            >
              {role.get('name')}
            </Typography>
            {(role.get('startingAt') || role.get('endingAt')) && (
              <Typography
                component="div"
                style={{
                  fontSize: 15,
                  fontWeight: 300,
                  marginBottom: 5,
                  display: 'flex',
                }}
              >
                <div style={{ color: black, minWidth: 150 }}>Duration:</div>
                <span style={{ fontWeight: 300 }}>
                  <DateRangeWithTimeZone
                    startingAt={role.get('startingAt')}
                    endingAt={role.get('endingAt')}
                    withTime
                    fontSize={15}
                    timeZone={deed.timeZone}
                  />
                </span>
              </Typography>
            )}
            {deed.type === 'Project' && (
              <>
                <Typography
                  component="div"
                  style={{
                    fontSize: 15,
                    fontWeight: 300,
                    marginBottom: 5,
                    display: 'flex',
                  }}
                >
                  <div
                    style={{
                      color: black,
                      minWidth: 150,
                    }}
                  >
                    Skills required:
                  </div>
                  <span style={{ marginLeft: 2, fontWeight: 300 }}>
                    {renderNeededSkills(role.get('skills'), skills)}
                  </span>
                </Typography>
              </>
            )}
            <Typography
              component="div"
              style={{
                fontSize: 15,
                fontWeight: 300,
                marginBottom: 5,
                display: 'flex',
              }}
            >
              <div style={{ color: black, minWidth: 150 }}>People needed:</div>
              <span style={{ fontWeight: 300 }}>
                {approved.size > 0 && `${approved.size} / `}
                {role.get('requiredAmount')}
              </span>
            </Typography>
            {deed.type === 'Project' && (
              <>
                <Typography
                  component="div"
                  style={{
                    fontSize: 15,
                    fontWeight: 300,
                    marginBottom: 5,
                    display: 'flex',
                  }}
                >
                  <div style={{ color: black, minWidth: 150 }}>Availability:</div>
                  <span style={{ marginLeft: 2, fontWeight: 300 }}>
                    {role.get('estimatedHoursMin')}
                    {role.get('estimatedHoursMax') !== role.get('estimatedHoursMin')
                      ? `-${role.get('estimatedHoursMax')}`
                      : ''}{' '}
                    hour
                    {role.get('estimatedHoursMax') > 1 ? 's' : ''} over {role.get('durationMin')}
                    {role.get('durationMax') !== role.get('durationMin')
                      ? `-${role.get('durationMax')}`
                      : ''}{' '}
                    {role.get('durationMax') > 1
                      ? role.get('durationUnit')
                      : role.get('durationUnit').slice(0, -1)}
                  </span>
                </Typography>
                <Typography
                  component="div"
                  style={{
                    fontSize: 15,
                    fontWeight: 300,
                    marginBottom: 5,
                    display: 'flex',
                  }}
                >
                  <div style={{ color: black, minWidth: 150 }}>Value:</div>
                  <span style={{ marginLeft: 2, fontWeight: 300 }}>
                    {currencyFormat(
                      role.get('estimatedHoursMin') * estimatedValue,
                      user.displayCurrency
                    )}
                    -
                    {currencyFormat(
                      role.get('estimatedHoursMax') * estimatedValue,
                      user.displayCurrency
                    )}
                  </span>
                </Typography>
              </>
            )}
            {role.get('description') && (
              <div
                style={{
                  marginBottom: 15,
                  display: 'flex',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 300,
                    color: black,
                    minWidth: 150,
                  }}
                >
                  Description:
                </div>
                <Typography
                  component="p"
                  style={{
                    fontSize: 15,
                    fontWeight: 300,
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                  }}
                >
                  <Linkify>
                    {role
                      .get('description')
                      .split('\n')
                      .map((item, key) => (
                        <span key={item || key}>
                          {item}
                          <br />
                        </span>
                      ))}
                  </Linkify>
                </Typography>
              </div>
            )}
            {/* **************************************** */}
            {/* Table of applicants for a specific role* */}
            {/* **************************************** */}
            <AttendeesTable
              attendees={[...approved, ...applicants, ...rejected]}
              deed={deed}
              organization={organization}
              volunteerTimes={
                // If there is no role, we want to show all volunteerTimes associated to the deed
                // If there is a role, we only want to show the volunteerTimes for this specific deed
                !role
                  ? volunteerTimes
                  : volunteerTimes.filter((vt) => {
                      // In theory every new vt here should have a roleId. However, VT's which don't
                      // have a roleId here should be from the past.
                      if (!vt.get('roleId')) {
                        return true;
                      }
                      return vt.get('roleId') === role.get('id');
                    })
              }
              role={role}
              title="Attendees"
            />
          </div>
        );
      })}
    </Box>
  );
};
